/**
 * One deal, end to end: what the brand wants, what the creator earns, what
 * happens next, the conversation, and the one button for the next step.
 *
 * `nextAction` from the API leads the page. A creator opening a deal wants
 * to know "what do I do now?" before anything else, and the server knows the
 * answer better than any status label the client could derive.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import { compressImage } from '../lib/image';
import {
  arr,
  big,
  cleanRupees,
  DEAL_STATUS,
  dealStatusLabel,
  isImageUrl,
  kindLabel,
  microsToRupees,
  needsYou,
  parseLinks,
  platformLabel,
  str,
  uploadFile,
  type Deal,
  type DealMessage,
} from '../lib/creator';
import { formatDue, formatINR, shortDate } from '../lib/format';
import {
  ActionBar,
  Card,
  ErrorNote,
  Field,
  Meta,
  MoneyRow,
  SectionTitle,
  Spinner,
  SubHeader,
} from '../components/Primitives';
import { Icon } from '../components/Icon';
import { AiHelper, Chip, PlatformDot, Sheet } from '../components/Creator';

interface DealResponse {
  deal: Deal;
  messages?: DealMessage[] | null;
  brand?: { name?: string | null } | null;
}

type SheetKind = 'accept' | 'decline' | 'draft' | 'live' | null;

const addDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function CreatorDealPage() {
  const { id } = useParams<{ id: string }>();
  const T = useT();
  const { lang } = useI18n();
  const [data, setData] = useState<DealResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetKind>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<DealResponse>(`/v1/partner/creator/deals/${id}`));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data && error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;

  const deal = data.deal;
  const messages = arr(data.messages);
  const brandName = data.brand?.name || deal.counterpart?.name || T('ब्रांड', 'Brand');
  const status = deal.status;
  const meta = DEAL_STATUS[status];
  const pkg = deal.package ?? null;
  const posts = pkg ? pkg.includesPosting !== false : true;
  const closed = status === 'declined' || status === 'cancelled';
  const gross = big(deal.grossMicros);
  const commission = big(deal.commissionMicros);
  const net = deal.netMicros ? big(deal.netMicros) : gross - commission;

  const done = (msg: string) => {
    setSheet(null);
    setFlash(msg);
    void load();
  };

  let primary: { label: string; onClick: () => void } | null = null;
  let secondary: { label: string; onClick: () => void } | null = null;
  if (status === 'requested') {
    primary = { label: T('मंज़ूर करें', 'Accept'), onClick: () => setSheet('accept') };
    secondary = { label: T('मना करें', 'Decline'), onClick: () => setSheet('decline') };
  } else if (status === 'in_progress' || status === 'changes_requested') {
    primary = {
      label:
        status === 'changes_requested'
          ? T('नया ड्राफ़्ट भेजें', 'Send revised draft')
          : T('ड्राफ़्ट भेजें', 'Upload draft'),
      onClick: () => setSheet('draft'),
    };
  } else if (status === 'approved' && posts) {
    primary = {
      label: T('लाइव लिंक भेजें', 'Submit live link'),
      onClick: () => setSheet('live'),
    };
  }

  return (
    <div className="space-y-4">
      <SubHeader back="/deals" title={deal.title || T('डील', 'Deal')} sub={brandName} />

      {/* What happens next — first, and loud. */}
      <section
        className="relative overflow-hidden rounded-[22px] p-4 text-white shadow-lift"
        style={{ backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className="rounded-full bg-white px-3 py-1 text-xs font-bold"
            style={{ color: 'var(--sec-ink)' }}
          >
            {dealStatusLabel(status, lang)}
          </span>
          {deal.dueOn && !closed && status !== 'completed' && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/90">
              <Icon name="clock" className="h-3.5 w-3.5" />
              {formatDue(deal.dueOn, lang)}
            </span>
          )}
        </div>
        <p className="mt-3 font-display text-[19px] font-bold leading-snug">
          {str(deal.nextAction) || fallbackNext(status, posts, T)}
        </p>
        {flash && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">
            <Icon name="check" className="h-3.5 w-3.5" strokeWidth={2.6} />
            {flash}
          </p>
        )}
      </section>

      {error && <ErrorNote message={error} onRetry={() => void load()} />}

      {/* Money */}
      <Card>
        <SectionTitle>{T('पैसा', 'Money')}</SectionTitle>
        {gross > 0n ? (
          <div className="divide-y divide-line rounded-2xl bg-panel-2 px-4 py-1">
            <MoneyRow label={T('तय दाम', 'Agreed price')} value={formatINR(gross)} muted />
            <MoneyRow
              label={T('लुलु कमीशन', 'Lulu commission')}
              value={`− ${formatINR(commission)}`}
              muted
            />
            <MoneyRow label={T('आपको मिलेगा*', 'You receive*')} value={formatINR(net)} strong />
          </div>
        ) : (
          <div className="rounded-2xl bg-panel-2 p-4">
            <p className="text-sm text-muted">
              {deal.kind === 'custom'
                ? T(
                    'यह कस्टम रिक्वेस्ट है — मंज़ूर करते समय अपना दाम डालें।',
                    'This is a custom request — set your price when you accept.',
                  )
                : T('दाम अभी तय नहीं।', 'Price not set yet.')}
            </p>
            {big(deal.budgetMicros) > 0n && (
              <p className="mt-1 text-sm">
                {T('ब्रांड का बजट:', 'Brand’s budget:')}{' '}
                <b className="tnum">{formatINR(big(deal.budgetMicros))}</b>
              </p>
            )}
          </div>
        )}
        {gross > 0n && (
          <p className="mt-2 text-xs text-muted">
            {T(
              '*TDS से पहले। ब्रांड GST अलग से देता है',
              '*Before TDS. The brand pays GST separately',
            )}
            {big(deal.gstMicros) > 0n ? ` (${formatINR(big(deal.gstMicros))})` : ''}.
          </p>
        )}
      </Card>

      {/* The brief */}
      <Card className="space-y-3">
        <SectionTitle>{T('ब्रांड क्या चाहता है', 'The brief')}</SectionTitle>
        {pkg && (
          <div className="flex items-center gap-3 rounded-2xl bg-panel-2 p-3">
            <PlatformDot platform={pkg.platform} className="h-9 w-9 text-xs" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{pkg.title}</p>
              <p className="text-xs text-muted">
                {platformLabel(pkg.platform, lang)} · {kindLabel(pkg.kind, lang)} ·{' '}
                {pkg.deliveryDays} {T('दिन', 'days')} · {pkg.revisions}{' '}
                {T('बदलाव', 'revisions')}
              </p>
            </div>
          </div>
        )}
        {str(deal.brief) ? (
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{deal.brief}</p>
        ) : (
          <p className="text-sm text-muted">
            {T('ब्रांड ने कोई ब्रीफ़ नहीं लिखा।', 'No brief written.')}
          </p>
        )}
        {arr(deal.deliverables).length > 0 && (
          <div>
            <p className="field-label">{T('क्या देना है', 'Deliverables')}</p>
            <ul className="space-y-1.5">
              {arr(deal.deliverables).map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
                  >
                    <Icon name="check" className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
        )}
        {str(deal.productNote) && (
          <div className="rounded-2xl bg-amber-50 p-3">
            <p className="text-xs font-semibold text-live">{T('प्रोडक्ट', 'Product')}</p>
            <p className="mt-0.5 text-sm">{deal.productNote}</p>
          </div>
        )}
        {arr(deal.referenceLinks).length > 0 && (
          <div>
            <p className="field-label">{T('रेफ़रेंस', 'References')}</p>
            <LinkList urls={arr(deal.referenceLinks)} />
          </div>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {deal.dueOn && (
            <Meta icon="clock">
              {T('तारीख', 'Due')} {shortDate(deal.dueOn, lang)}
            </Meta>
          )}
          <Meta icon="jobs">
            {deal.kind === 'custom'
              ? T('कस्टम रिक्वेस्ट', 'Custom request')
              : T('पैकेज', 'Package')}
          </Meta>
          <Meta icon="bookings">
            {T('आई', 'Received')} {shortDate(deal.createdAt, lang)}
          </Meta>
        </div>
      </Card>

      {arr(deal.liveUrls).length > 0 && (
        <Card>
          <SectionTitle>{T('लाइव लिंक', 'Live links')}</SectionTitle>
          <LinkList urls={arr(deal.liveUrls)} />
        </Card>
      )}

      {arr(deal.liveUrls).length > 0 && <ResultsCard results={deal.results} />}

      {!closed && status !== 'completed' && (
        <Card className="space-y-3">
          <SectionTitle>{T('AI मदद', 'AI help')}</SectionTitle>
          <AiHelper
            task="ideas"
            label={T('इस ब्रीफ़ के लिए कंटेंट आइडिया', 'Content ideas for this brief')}
            input={{
              title: deal.title,
              brief: deal.brief,
              deliverables: arr(deal.deliverables),
              productNote: deal.productNote,
              brand: brandName,
              package: pkg
                ? { platform: pkg.platform, kind: pkg.kind, title: pkg.title }
                : null,
              outputLanguage: lang === 'hi' ? 'hinglish' : 'english',
            }}
          />
          {status === 'requested' && (
            <AiHelper
              task="pitch"
              label={T('ब्रांड को जवाब/पिच लिखें', 'Write my pitch to the brand')}
              input={{
                title: deal.title,
                brief: deal.brief,
                brand: brandName,
                deliverables: arr(deal.deliverables),
                outputLanguage: lang === 'hi' ? 'hinglish' : 'english',
              }}
            />
          )}
        </Card>
      )}

      <Thread
        dealId={deal.id}
        messages={messages}
        brandName={brandName}
        canSend={!closed}
        onSent={() => void load()}
      />

      {(primary || secondary) && (
        <ActionBar>
          <div className="flex gap-2">
            {secondary && (
              <button
                type="button"
                className="btn-secondary !w-auto flex-1"
                onClick={secondary.onClick}
              >
                {secondary.label}
              </button>
            )}
            {primary && (
              <button
                type="button"
                className={`btn-primary ${secondary ? '!w-auto flex-[2]' : ''}`}
                onClick={primary.onClick}
              >
                {needsYou(status) && <Icon name="arrow" className="h-5 w-5" />}
                {primary.label}
              </button>
            )}
          </div>
        </ActionBar>
      )}

      {sheet === 'accept' && (
        <AcceptSheet
          deal={deal}
          onClose={() => setSheet(null)}
          onDone={() =>
            done(
              T(
                'मंज़ूर किया — ब्रांड के पेमेंट का इंतज़ार',
                'Accepted — waiting for the brand to pay',
              ),
            )
          }
        />
      )}
      {sheet === 'decline' && (
        <DeclineSheet
          deal={deal}
          onClose={() => setSheet(null)}
          onDone={() => done(T('मना कर दिया', 'Declined'))}
        />
      )}
      {sheet === 'draft' && (
        <DraftSheet
          deal={deal}
          onClose={() => setSheet(null)}
          onDone={() => done(T('ड्राफ़्ट भेजा गया', 'Draft sent'))}
        />
      )}
      {sheet === 'live' && (
        <LiveSheet
          deal={deal}
          onClose={() => setSheet(null)}
          onDone={() => done(T('लाइव लिंक भेजा गया', 'Live link sent'))}
        />
      )}

      {meta?.tone === 'bad' && (
        <p className="rounded-2xl bg-rose-50 p-3 text-sm text-bad">
          {T(
            'इस डील पर शिकायत है। पेमेंट रुका है; लुलु की टीम दोनों से बात करेगी।',
            'This deal is disputed. The payout is on hold while Lulu’s team talks to both sides.',
          )}
        </p>
      )}
    </div>
  );
}

function fallbackNext(
  status: string,
  posts: boolean,
  T: (hi: string, en: string) => string,
): string {
  switch (status) {
    case 'requested':
      return T(
        'ब्रीफ़ पढ़ें, फिर मंज़ूर या मना करें।',
        'Read the brief, then accept or decline.',
      );
    case 'accepted':
      return T(
        'ब्रांड के पेमेंट का इंतज़ार। पेमेंट होते ही हम बताएँगे।',
        'Waiting for the brand to pay. We’ll tell you as soon as they do.',
      );
    case 'in_progress':
      return T(
        'पेमेंट हो गया। कंटेंट बनाएँ और ड्राफ़्ट भेजें।',
        'Paid. Make the content and upload your draft.',
      );
    case 'draft_submitted':
      return T('ब्रांड आपका ड्राफ़्ट देख रहा है।', 'The brand is reviewing your draft.');
    case 'changes_requested':
      return T(
        'ब्रांड ने बदलाव माँगे हैं — मैसेज देखें और नया ड्राफ़्ट भेजें।',
        'The brand asked for changes — see the messages and send a new draft.',
      );
    case 'approved':
      return posts
        ? T(
            'ड्राफ़्ट मंज़ूर! पोस्ट करें और लाइव लिंक भेजें।',
            'Draft approved! Post it and submit the live link.',
          )
        : T('मंज़ूर! पेमेंट जारी हो रहा है।', 'Approved! Your payout is being released.');
    case 'live':
      return T(
        'ब्रांड पुष्टि करेगा (या 3 दिन बाद अपने आप पूरा)।',
        'The brand confirms (or it completes on its own after 3 days).',
      );
    case 'completed':
      return T(
        'डील पूरी। पेमेंट "कमाई" में दिखेगा।',
        'Deal complete. Your payout shows under Earnings.',
      );
    case 'declined':
      return T('आपने यह डील मना की।', 'You declined this deal.');
    case 'cancelled':
      return T('ब्रांड ने यह डील रद्द की।', 'The brand cancelled this deal.');
    case 'disputed':
      return T(
        'शिकायत पर लुलु की टीम काम कर रही है।',
        'Lulu’s team is looking into a dispute.',
      );
    default:
      return '';
  }
}

interface PostResult {
  url: string;
  platform: string;
  likes: number | null;
  comments: number | null;
  views: number | null;
  error?: string;
}

/**
 * The public likes, comments and views Lulu read for the live posts (the
 * brand sees the same card). Read-only here: Lulu reads them a day and a
 * half after going live and again at completion; the brand can refresh.
 */
function ResultsCard({ results }: { results: unknown }) {
  const T = useT();
  const { lang } = useI18n();
  const r =
    results && typeof results === 'object' && !Array.isArray(results)
      ? (results as {
          fetchedAt?: unknown;
          posts?: unknown;
          totals?: { likes: number | null; comments: number | null; views: number | null };
        })
      : null;
  const posts = Array.isArray(r?.posts)
    ? (r?.posts as PostResult[]).filter((p) => p && typeof p.url === 'string')
    : [];
  const fetchedAt = typeof r?.fetchedAt === 'string' ? r.fetchedAt : null;
  const fmt = (n: number | null | undefined) =>
    typeof n === 'number' ? n.toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN') : '—';
  return (
    <Card className="space-y-3">
      <SectionTitle>{T('नतीजे', 'Results')}</SectionTitle>
      {!fetchedAt ? (
        <p className="text-sm text-muted">
          {T(
            'पोस्ट लाइव होने के डेढ़ दिन बाद और डील पूरी होने पर लुलु पब्लिक लाइक, कमेंट और व्यू पढ़ता है।',
            'Lulu reads the public likes, comments and views a day and a half after the post goes live, and again when the deal completes.',
          )}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                [T('लाइक', 'Likes'), r?.totals?.likes ?? null],
                [T('कमेंट', 'Comments'), r?.totals?.comments ?? null],
                [T('व्यू', 'Views'), r?.totals?.views ?? null],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-panel-2 px-3 py-2">
                <div className="text-[11px] text-muted">{k}</div>
                <div className="font-display text-lg font-bold">
                  {v === null ? (
                    <span className="text-xs font-normal text-muted">
                      {T('उपलब्ध नहीं', 'not available')}
                    </span>
                  ) : (
                    fmt(v)
                  )}
                </div>
              </div>
            ))}
          </div>
          <ul className="space-y-1.5">
            {posts.map((p) => (
              <li key={p.url} className="rounded-xl bg-panel-2 px-3 py-2 text-sm">
                <div className="truncate font-medium">
                  {p.url.replace(/^https?:\/\/(www\.)?/, '')}
                </div>
                <div className="text-xs text-muted">
                  {p.error
                    ? `${T('उपलब्ध नहीं', 'Not available')}: ${p.error}`
                    : `${fmt(p.likes)} ${T('लाइक', 'likes')} · ${fmt(p.comments)} ${T('कमेंट', 'comments')}${
                        p.views !== null ? ` · ${fmt(p.views)} ${T('व्यू', 'views')}` : ''
                      }`}
                </div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted">
            {T('पढ़ा गया', 'Read')} {shortDate(fetchedAt, lang)}
          </p>
        </>
      )}
    </Card>
  );
}

function LinkList({ urls }: { urls: string[] }) {
  return (
    <ul className="space-y-1.5">
      {urls.map((u, i) => (
        <li key={`${u}-${i}`}>
          <a
            href={u}
            target="_blank"
            rel="noreferrer noopener"
            className="flex min-h-[44px] items-center gap-2 rounded-xl bg-panel-2 px-3 text-sm font-medium"
            style={{ color: 'var(--sec-ink)' }}
          >
            <Icon name="link" className="h-4 w-4 shrink-0" />
            <span className="truncate">{u.replace(/^https?:\/\/(www\.)?/, '')}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------- thread

function Thread({
  dealId,
  messages,
  brandName,
  canSend,
  onSent,
}: {
  dealId: string;
  messages: DealMessage[];
  brandName: string;
  canSend: boolean;
  onSent: () => void;
}) {
  const T = useT();
  const { lang } = useI18n();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/partner/creator/deals/${dealId}/message`, { body: text });
      setBody('');
      onSent();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('मैसेज नहीं गया।', 'Message not sent.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const kindTag: Record<string, string> = {
    draft: T('ड्राफ़्ट', 'Draft'),
    live: T('लाइव लिंक', 'Live link'),
  };

  return (
    <Card>
      <SectionTitle count={messages.length || undefined}>
        {T('बातचीत', 'Messages')}
      </SectionTitle>
      {messages.length === 0 ? (
        <p className="py-3 text-center text-sm text-muted">
          {T(
            `${brandName} से यहीं बात करें, ताकि हर बात का रिकॉर्ड रहे।`,
            `Talk to ${brandName} here, so there is a record of everything agreed.`,
          )}
        </p>
      ) : (
        <ol className="space-y-3">
          {messages.map((m) => {
            if (m.author === 'system' || m.kind === 'status')
              return (
                <li key={m.id} className="text-center">
                  <span className="inline-block rounded-full bg-panel-2 px-3 py-1 text-xs text-muted">
                    {m.body} · {shortDate(m.createdAt, lang)}
                  </span>
                </li>
              );
            const mine = m.author === 'creator';
            return (
              <li key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${mine ? 'rounded-br-md text-white' : 'rounded-bl-md bg-panel-2'}`}
                  style={
                    mine
                      ? {
                          backgroundImage:
                            'linear-gradient(135deg, var(--sec-from), var(--sec-to))',
                        }
                      : undefined
                  }
                >
                  {kindTag[m.kind] && (
                    <p className={`eyebrow mb-1 ${mine ? 'text-white/80' : 'text-muted'}`}>
                      {kindTag[m.kind]}
                    </p>
                  )}
                  {str(m.body) && (
                    <p className="whitespace-pre-wrap text-[15px] leading-snug">{m.body}</p>
                  )}
                  {arr(m.files).length > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {arr(m.files).map((f, i) =>
                        isImageUrl(f) ? (
                          <a key={i} href={f} target="_blank" rel="noreferrer">
                            <img
                              src={f}
                              alt=""
                              className="h-28 w-full rounded-xl object-cover"
                            />
                          </a>
                        ) : (
                          <a
                            key={i}
                            href={f}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={`col-span-2 flex items-center gap-1.5 truncate rounded-xl px-2 py-1.5 text-xs font-semibold ${mine ? 'bg-white/20' : 'bg-panel'}`}
                          >
                            <Icon
                              name={
                                /^https?:/.test(f) && !f.includes('/v1/files/')
                                  ? 'link'
                                  : 'file'
                              }
                              className="h-4 w-4 shrink-0"
                            />
                            <span className="truncate">
                              {f.replace(/^https?:\/\/(www\.)?/, '')}
                            </span>
                          </a>
                        ),
                      )}
                    </div>
                  )}
                  <p
                    className={`mt-1 text-right text-[11px] ${mine ? 'text-white/75' : 'text-muted'}`}
                  >
                    {mine ? T('आप', 'You') : brandName} ·{' '}
                    {new Date(m.createdAt).toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN', {
                      day: 'numeric',
                      month: 'short',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {canSend && (
        <div className="mt-3 flex items-end gap-2">
          <textarea
            rows={1}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 2000))}
            placeholder={T('मैसेज लिखें…', 'Write a message…')}
            className="max-h-40 min-h-[48px] resize-none"
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
            }}
          />
          <button
            type="button"
            aria-label={T('भेजें', 'Send')}
            onClick={() => void send()}
            disabled={busy || !body.trim()}
            className="icon-tile h-12 w-12 rounded-2xl disabled:opacity-50"
          >
            <Icon name="send" className="h-5 w-5" />
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-bad">{error}</p>}
    </Card>
  );
}

// ------------------------------------------------------------------ sheets

function AcceptSheet({
  deal,
  onClose,
  onDone,
}: {
  deal: Deal;
  onClose: () => void;
  onDone: () => void;
}) {
  const T = useT();
  const custom = deal.kind === 'custom' || !deal.package;
  const initialPrice =
    big(deal.grossMicros) > 0n
      ? microsToRupees(deal.grossMicros)
      : big(deal.budgetMicros) > 0n
        ? microsToRupees(deal.budgetMicros)
        : deal.package
          ? microsToRupees(deal.package.priceMicros)
          : '';
  const [price, setPrice] = useState(initialPrice);
  const [dueOn, setDueOn] = useState(deal.dueOn ?? addDays(deal.package?.deliveryDays ?? 7));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clean = cleanRupees(price);
  const n = Number(clean || 0);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/partner/creator/deals/${deal.id}/accept`, {
        ...(clean ? { price: clean } : {}),
        ...(dueOn ? { dueOn } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('मंज़ूर नहीं हुआ।', 'Could not accept.'),
      );
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      sec="deals"
      title={T('डील मंज़ूर करें', 'Accept deal')}
      footer={
        <div className="space-y-2 pb-1">
          {error && <p className="text-sm text-bad">{error}</p>}
          <button
            type="button"
            className="btn-primary"
            disabled={busy || (custom && n <= 0)}
            onClick={() => void submit()}
          >
            {busy
              ? T('भेज रहे हैं…', 'Sending…')
              : n > 0
                ? T(
                    `${formatINR(String(Math.round(n * 1e6)))} पर मंज़ूर करें`,
                    `Accept at ${formatINR(String(Math.round(n * 1e6)))}`,
                  )
                : T('मंज़ूर करें', 'Accept')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field
          label={
            custom
              ? T('आपका दाम (₹, GST के बिना)', 'Your price (₹, before GST)')
              : T('दाम (₹)', 'Price (₹)')
          }
          hint={
            n > 0
              ? T(
                  `आपको मिलेगा लगभग ${formatINR(String(Math.round(n * 0.9 * 1e6)))} (10% कमीशन के बाद, TDS से पहले)`,
                  `You receive about ${formatINR(String(Math.round(n * 0.9 * 1e6)))} (after 10% commission, before TDS)`,
                )
              : custom
                ? T(
                    'कस्टम रिक्वेस्ट के लिए दाम ज़रूरी है',
                    'A price is required for custom requests',
                  )
                : undefined
          }
        >
          <input
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))}
            className="tnum !text-xl font-bold"
            readOnly={!custom && Boolean(deal.package)}
          />
        </Field>
        {!custom && (
          <p className="-mt-2 text-xs text-muted">
            {T('यह आपके पैकेज का दाम है।', 'This is your package price.')}
          </p>
        )}
        {big(deal.budgetMicros) > 0n && (
          <p className="-mt-2 text-xs text-muted">
            {T('ब्रांड का बजट:', 'Brand’s budget:')}{' '}
            <b className="tnum">{formatINR(big(deal.budgetMicros))}</b>
          </p>
        )}
        <Field label={T('कब तक दे देंगे', 'Deliver by')}>
          <input
            type="date"
            value={dueOn}
            min={addDays(1)}
            onChange={(e) => setDueOn(e.target.value)}
          />
        </Field>
        <Field
          label={`${T('ब्रांड के लिए नोट', 'Note to the brand')} (${T('ज़रूरी नहीं', 'optional')})`}
        >
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            placeholder={T(
              'जैसे: प्रोडक्ट मिलते ही 3 दिन में ड्राफ़्ट भेज दूँगी।',
              'e.g. I will send the draft within 3 days of receiving the product.',
            )}
          />
        </Field>
        <AiHelper
          task="pitch"
          label={T('AI से नोट लिखवाएँ', 'Draft this note with AI')}
          applyLabel={T('नोट में लगाएँ', 'Use as note')}
          onApply={(t) => setNote(t.slice(0, 1000))}
          input={{
            title: deal.title,
            brief: deal.brief,
            deliverables: arr(deal.deliverables),
            price: clean || null,
            dueOn,
          }}
        />
        <p className="rounded-2xl bg-panel-2 p-3 text-xs text-muted">
          {T(
            'मंज़ूर करने के बाद ब्रांड पेमेंट करेगा। पेमेंट होने पर ही काम शुरू करें — पैसा लुलु के पास सुरक्षित रहता है।',
            'After you accept, the brand pays. Start work only once it is paid — the money is held safely by Lulu.',
          )}
        </p>
      </div>
    </Sheet>
  );
}

function DeclineSheet({
  deal,
  onClose,
  onDone,
}: {
  deal: Deal;
  onClose: () => void;
  onDone: () => void;
}) {
  const T = useT();
  const reasons = [
    T('बजट कम है', 'Budget too low'),
    T('मेरी कैटेगरी से मेल नहीं खाता', 'Not a fit for my audience'),
    T('इन तारीखों पर समय नहीं', 'No time on these dates'),
    T('इसी तरह के ब्रांड के साथ काम चल रहा है', 'Working with a competing brand'),
  ];
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/partner/creator/deals/${deal.id}/decline`, { reason: reason.trim() });
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('नहीं हो पाया।', 'Could not decline.'),
      );
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      sec="deals"
      title={T('डील मना करें', 'Decline deal')}
      footer={
        <div className="space-y-2 pb-1">
          {error && <p className="text-sm text-bad">{error}</p>}
          <button
            type="button"
            className="btn-primary"
            disabled={busy || reason.trim().length < 3}
            onClick={() => void submit()}
          >
            {busy ? T('भेज रहे हैं…', 'Sending…') : T('मना करें', 'Decline')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          {T(
            'ब्रांड को एक लाइन में कारण बताएँ — अगली बार बेहतर ऑफ़र आता है।',
            'Give the brand a one-line reason — it gets you better offers next time.',
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {reasons.map((r) => (
            <Chip key={r} on={reason === r} onClick={() => setReason(r)}>
              {r}
            </Chip>
          ))}
        </div>
        <Field label={T('कारण', 'Reason')}>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
          />
        </Field>
      </div>
    </Sheet>
  );
}

function DraftSheet({
  deal,
  onClose,
  onDone,
}: {
  deal: Deal;
  onClose: () => void;
  onDone: () => void;
}) {
  const T = useT();
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [uploading, setUploading] = useState(0);
  const [links, setLinks] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = parseLinks(links);

  async function onFiles(list: FileList) {
    setError(null);
    for (const file of Array.from(list).slice(0, 10)) {
      if (file.size > 8_000_000 && !file.type.startsWith('image/')) {
        setError(
          T(
            'वीडियो बड़ा है — Google Drive/YouTube (अनलिस्टेड) का लिंक डालें।',
            'That video is too big — paste a Google Drive or unlisted YouTube link instead.',
          ),
        );
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const { dataUrl } = await compressImage(file);
        const url = await uploadFile(dataUrl);
        setFiles((f) => [...f, url]);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : T('फ़ाइल नहीं गई।', 'A file failed to upload.'),
        );
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (input.current) input.current.value = '';
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/partner/creator/deals/${deal.id}/draft`, {
        files,
        ...(parsed.length ? { links: parsed } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('ड्राफ़्ट नहीं गया।', 'Draft not sent.'),
      );
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      sec="deals"
      title={T('ड्राफ़्ट भेजें', 'Upload draft')}
      footer={
        <div className="space-y-2 pb-1">
          {error && <p className="text-sm text-bad">{error}</p>}
          <button
            type="button"
            className="btn-primary"
            disabled={busy || uploading > 0 || (files.length === 0 && parsed.length === 0)}
            onClick={() => void submit()}
          >
            {busy ? T('भेज रहे हैं…', 'Sending…') : T('ब्रांड को भेजें', 'Send to brand')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          {T(
            'ब्रांड पोस्ट होने से पहले इसे देखेगा और मंज़ूर करेगा या बदलाव माँगेगा।',
            'The brand reviews this before anything goes live, then approves or asks for changes.',
          )}
        </p>
        <input
          ref={input}
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          className="hidden"
          onChange={(e) => e.target.files && void onFiles(e.target.files)}
        />
        <button
          type="button"
          className="btn-secondary min-h-[64px]"
          onClick={() => input.current?.click()}
          disabled={uploading > 0}
        >
          <Icon name="camera" className="h-5 w-5" />
          {uploading > 0
            ? T('अपलोड हो रहा है…', 'Uploading…')
            : T('फ़ोटो / फ़ाइल चुनें', 'Choose photos / files')}
        </button>
        {files.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {files.map((f, i) => (
              <div key={f} className="relative">
                {isImageUrl(f) ? (
                  <img src={f} alt="" className="h-24 w-full rounded-xl object-cover" />
                ) : (
                  <span className="flex h-24 items-center justify-center rounded-xl bg-panel-2 text-muted">
                    <Icon name="file" className="h-7 w-7" />
                  </span>
                )}
                <button
                  type="button"
                  aria-label={T('हटाएँ', 'Remove')}
                  onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}
                  className="absolute right-1 top-1 flex !min-h-0 h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow"
                >
                  <Icon name="x" className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <Field
          label={T('या वीडियो का लिंक', 'Or video links')}
          hint={T(
            'Google Drive, YouTube (अनलिस्टेड)… हर लाइन में एक',
            'Google Drive, unlisted YouTube… one per line',
          )}
        >
          <textarea
            rows={2}
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            inputMode="url"
            autoCapitalize="none"
          />
        </Field>
        <Field label={`${T('नोट', 'Note')} (${T('ज़रूरी नहीं', 'optional')})`}>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            placeholder={T(
              'कैप्शन, म्यूज़िक, पोस्ट करने का समय…',
              'Caption, music, when you plan to post…',
            )}
          />
        </Field>
      </div>
    </Sheet>
  );
}

function LiveSheet({
  deal,
  onClose,
  onDone,
}: {
  deal: Deal;
  onClose: () => void;
  onDone: () => void;
}) {
  const T = useT();
  const [links, setLinks] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urls = parseLinks(links);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/partner/creator/deals/${deal.id}/live`, { urls });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : T('लिंक नहीं गया।', 'Could not send.'));
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      sec="deals"
      title={T('लाइव लिंक भेजें', 'Submit live links')}
      footer={
        <div className="space-y-2 pb-1">
          {error && <p className="text-sm text-bad">{error}</p>}
          <button
            type="button"
            className="btn-primary"
            disabled={busy || urls.length === 0}
            onClick={() => void submit()}
          >
            {busy ? T('भेज रहे हैं…', 'Sending…') : T('ब्रांड को भेजें', 'Send to brand')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          {T(
            'पोस्ट करने के बाद उसका लिंक डालें। ब्रांड पुष्टि करेगा, या 3 दिन में डील अपने आप पूरी होगी और पेमेंट जारी होगा।',
            'Paste the link to each post. The brand confirms — or after 3 days the deal completes on its own and your payout is released.',
          )}
        </p>
        <Field
          label={T('पोस्ट के लिंक', 'Post links')}
          hint={T('हर लाइन में एक', 'One per line')}
        >
          <textarea
            rows={4}
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            inputMode="url"
            autoCapitalize="none"
            placeholder="https://instagram.com/reel/…"
          />
        </Field>
        {urls.length > 0 && (
          <ul className="space-y-1 text-xs text-muted">
            {urls.map((u) => (
              <li key={u} className="flex items-center gap-1.5 truncate">
                <Icon name="link" className="h-3.5 w-3.5 shrink-0" />
                {u}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
